// Ported from files/snapdraw-design-kit/code/src/lib/i18n/strings/clients.ts.
// clients.back/documents/openInList reused as-is; added keys for the real
// (derived-from-documents) data model, which has no separate "contact
// person" or "payment terms" fields the kit's fixture data assumed.
// 2026-08-28 (backlog DOCS-23): clients.new.error.email claimed an invalid
// email would block "documents being delivered" — SnapDraw has no email-
// delivery feature at all (see docs.sent.body's own honest disclaimer,
// "SnapDraw does not send anything for you"). Replaced with plain
// validation copy, no capability claim.
export const en = {
  "clients.eyebrow": "Directory",
  "clients.title": "Clients",
  "clients.searchPlaceholder": "Search clients, email, phone…",
  "clients.totalOutstanding": "Total Outstanding",
  "clients.add": "Add",
  "clients.loading": "Loading clients",
  "clients.empty.title": "No clients yet",
  "clients.empty.body": "Add a client name to your first quote or pay application and they'll show up here automatically.",
  "clients.noContactYet": "No contact info yet",
  "clients.card.outstanding": "Outstanding",
  "clients.card.openDocs": "Open Docs",
  "clients.notFound.title": "Client not found",
  "clients.notFound.body": "That client no longer exists or the link is out of date.",
  "clients.notFound.back": "Back to clients",
  "clients.back": "All clients",
  "clients.newForClient": "New for this client",
  "clients.totalDocsSingular": "{count} document total",
  "clients.totalDocsPlural": "{count} documents total",
  "clients.stat.outstanding": "Outstanding",
  "clients.stat.openDocuments": "Open Documents",
  "clients.stat.primaryContact": "Primary Contact",
  "clients.documents": "Documents",
  "clients.openInList": "Open in list",
  "clients.docsEmpty.title": "No documents yet",
  "clients.docsEmpty.body": "Quotes and pay applications billed to this client will appear here.",
  "clients.new.eyebrow": "Directory",
  "clients.new.title": "New Client",
  "clients.new.subtitle": "Details entered here auto-fill on every quote and pay application.",
  "clients.new.back": "Back",
  "clients.new.panel.company": "Company",
  "clients.new.panel.contact": "Primary Contact",
  "clients.new.panel.billing": "Billing Defaults",
  "clients.new.field.name": "Company Name",
  "clients.new.field.contact": "Contact Name",
  "clients.new.field.email": "Email",
  "clients.new.field.phone": "Phone",
  "clients.new.field.city": "City, State",
  "clients.new.field.address": "Billing Address",
  "clients.new.field.notes": "Notes",
  "clients.new.field.retainage": "Retainage %",
  "clients.new.placeholder.name": "Kestrel Construction",
  "clients.new.placeholder.contact": "Dana Whitfield",
  "clients.new.placeholder.email": "dana@kestrelcc.com",
  "clients.new.placeholder.phone": "(312) 555-0164",
  "clients.new.placeholder.city": "Chicago, IL",
  "clients.new.placeholder.address": "480 N Wells St, Suite 300",
  "clients.new.placeholder.notes": "Gate code, PM preferences, billing portal…",
  "clients.new.error.name": "Company name is required.",
  "clients.new.error.email": "Enter a valid email address.",
  "clients.new.preview": "Preview",
  "clients.new.previewHint": "This is how the client appears in your directory.",
  "clients.new.save": "Save Client",
  "clients.new.saveAndDoc": "Save & Start Document",
  "clients.new.cancel": "Cancel",
  "clients.new.required": "Required",
  "clients.new.retainageHint": "Held back on each pay application until release.",
  "clients.new.retainageSuffix": "RETAINAGE",
  "clients.quickAdd.title": "New Client",
  "clients.quickAdd.body": "Add just the name for now — you can fill in contact and billing details later from the client's own page.",
  "clients.quickAdd.confirm": "Add Client",
  "clients.picker.label": "Select existing client (optional)",
  "clients.picker.manual": "— Enter manually —",
  "clients.picker.newClient": "+ New Client",
  "clients.picker.autofilled": "Auto-filled from {name} — edit anything below as needed.",
  "clients.edit.button": "Edit",
  "clients.edit.eyebrow": "Client",
  "clients.edit.title": "Edit Client",
  "clients.edit.subtitle": "Changes apply to new quotes and pay applications, not documents already sent.",
  "clients.edit.back": "All clients",
  "clients.edit.save": "Save Changes",
  "clients.edit.toast": "{name} updated",
} as const;

export const es: Record<keyof typeof en, string> = {
  "clients.eyebrow": "Directorio",
  "clients.title": "Clientes",
  "clients.searchPlaceholder": "Buscar clientes, correo, teléfono…",
  "clients.totalOutstanding": "Saldo Pendiente Total",
  "clients.add": "Agregar",
  "clients.loading": "Cargando clientes",
  "clients.empty.title": "Aún no hay clientes",
  "clients.empty.body": "Agrega el nombre de un cliente a tu primera cotización o solicitud de pago y aparecerá aquí automáticamente.",
  "clients.noContactYet": "Aún no hay datos de contacto",
  "clients.card.outstanding": "Saldo pendiente",
  "clients.card.openDocs": "Docs abiertos",
  "clients.notFound.title": "Cliente no encontrado",
  "clients.notFound.body": "Ese cliente ya no existe o el enlace está desactualizado.",
  "clients.notFound.back": "Volver a clientes",
  "clients.back": "Todos los clientes",
  "clients.newForClient": "Nuevo para este cliente",
  "clients.totalDocsSingular": "{count} documento en total",
  "clients.totalDocsPlural": "{count} documentos en total",
  "clients.stat.outstanding": "Saldo pendiente",
  "clients.stat.openDocuments": "Documentos abiertos",
  "clients.stat.primaryContact": "Contacto principal",
  "clients.documents": "Documentos",
  "clients.openInList": "Ver en lista",
  "clients.docsEmpty.title": "Aún no hay documentos",
  "clients.docsEmpty.body": "Las cotizaciones y solicitudes de pago facturadas a este cliente aparecerán aquí.",
  "clients.new.eyebrow": "Directorio",
  "clients.new.title": "Nuevo Cliente",
  "clients.new.subtitle": "Estos datos se completan solos en cada cotización y estimación de pago.",
  "clients.new.back": "Atrás",
  "clients.new.panel.company": "Empresa",
  "clients.new.panel.contact": "Contacto principal",
  "clients.new.panel.billing": "Valores de facturación",
  "clients.new.field.name": "Nombre de la empresa",
  "clients.new.field.contact": "Nombre del contacto",
  "clients.new.field.email": "Correo",
  "clients.new.field.phone": "Teléfono",
  "clients.new.field.city": "Ciudad, Estado",
  "clients.new.field.address": "Dirección de facturación",
  "clients.new.field.notes": "Notas",
  "clients.new.field.retainage": "Retención %",
  "clients.new.placeholder.name": "Kestrel Construction",
  "clients.new.placeholder.contact": "Dana Whitfield",
  "clients.new.placeholder.email": "dana@kestrelcc.com",
  "clients.new.placeholder.phone": "(312) 555-0164",
  "clients.new.placeholder.city": "Chicago, IL",
  "clients.new.placeholder.address": "480 N Wells St, Suite 300",
  "clients.new.placeholder.notes": "Código de acceso, preferencias del PM, portal…",
  "clients.new.error.name": "El nombre de la empresa es obligatorio.",
  "clients.new.error.email": "Ingresa una dirección de correo válida.",
  "clients.new.preview": "Vista previa",
  "clients.new.previewHint": "Así aparece el cliente en tu directorio.",
  "clients.new.save": "Guardar cliente",
  "clients.new.saveAndDoc": "Guardar e iniciar documento",
  "clients.new.cancel": "Cancelar",
  "clients.new.required": "Obligatorio",
  "clients.new.retainageHint": "Se retiene en cada estimación hasta su liberación.",
  "clients.new.retainageSuffix": "RETENCIÓN",
  "clients.quickAdd.title": "Nuevo Cliente",
  "clients.quickAdd.body": "Agrega solo el nombre por ahora — puedes completar los datos de contacto y facturación más tarde desde la página del cliente.",
  "clients.quickAdd.confirm": "Agregar Cliente",
  "clients.picker.label": "Seleccionar cliente existente (opcional)",
  "clients.picker.manual": "— Escribir manualmente —",
  "clients.picker.newClient": "+ Nuevo cliente",
  "clients.picker.autofilled": "Autocompletado desde {name} — edita lo que necesites abajo.",
  "clients.edit.button": "Editar",
  "clients.edit.eyebrow": "Cliente",
  "clients.edit.title": "Editar cliente",
  "clients.edit.subtitle": "Los cambios aplican a documentos nuevos, no a los ya enviados.",
  "clients.edit.back": "Todos los clientes",
  "clients.edit.save": "Guardar cambios",
  "clients.edit.toast": "{name} actualizado",
};
